import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { signInWithRedirect } from 'aws-amplify/auth';
import { Box, CircularProgress, Typography } from '@mui/material';

import { SSO_POPUP_FLAG, createAuthLoader, useAuthConfig } from './authUtils';

export const loader = createAuthLoader;

const STATUS = {
	INITIALIZING: 'initializing',
	REDIRECTING: 'redirecting',
	ERROR: 'error',
};

// Provider display names for user-friendly messages
const PROVIDER_DISPLAY_NAMES = {
	MicrosoftEntra: 'Microsoft',
	Google: 'Google',
};

// Native providers supported directly by Amplify
const NATIVE_PROVIDERS = ['Amazon', 'Apple', 'Facebook', 'Google'];

const resolveAmplifyProvider = (provider) => {
	const isNativeProvider = NATIVE_PROVIDERS.includes(provider);
	return isNativeProvider ? provider : { custom: provider };
};

const getStatusMessage = (status, provider) => {
	const displayName = PROVIDER_DISPLAY_NAMES[provider] || provider;

	switch (status) {
		case STATUS.INITIALIZING:
			return 'Initializing...';
		case STATUS.REDIRECTING:
			return `Redirecting to ${displayName}...`;
		case STATUS.ERROR:
			return 'Authentication failed. This window will close.';
		default:
			return 'Please wait...';
	}
};

const useSSORedirect = (ready, authConfig, provider) => {
	const [status, setStatus] = useState(STATUS.INITIALIZING);

	useEffect(() => {
		if (!ready || !authConfig || !provider) return;

		const triggerSSO = async () => {
			try {
				setStatus(STATUS.REDIRECTING);
				sessionStorage.setItem(SSO_POPUP_FLAG, 'true');
				const providerArg = resolveAmplifyProvider(provider);
				await signInWithRedirect({ provider: providerArg });
			} catch (err) {
				console.error('SSO redirect error:', err);
				sessionStorage.removeItem(SSO_POPUP_FLAG);
				setStatus(STATUS.ERROR);
				setTimeout(() => window.close(), 2000);
			}
		};

		triggerSSO();
	}, [ready, authConfig, provider]);

	return status;
};

const SSOHandler = () => {
	const { provider } = useParams();
	const { authConfig, ready } = useAuthConfig();

	const hasProvider = authConfig?.oauth?.providers?.includes(provider);
	const status = useSSORedirect(ready, authConfig, hasProvider ? provider : null);
	const providerName = provider;

	return (
		<Box
			display='flex'
			flexDirection='column'
			justifyContent='center'
			alignItems='center'
			height='100vh'
			gap={2}
		>
			{status !== STATUS.ERROR && <CircularProgress />}
			<Typography variant='body1'>
				{getStatusMessage(status, providerName)}
			</Typography>
		</Box>
	);
};

export default SSOHandler;
